// Dashboard.jsx - Final, production-ready, fully sanity-checked
import { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import {
  UserGroupIcon,
  CubeIcon,
  ArrowDownTrayIcon,
} from "@heroicons/react/24/outline";
import { apiClient } from "../apiClient";

function Dashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [period, setPeriod] = useState("all");
  const [productionRuns, setProductionRuns] = useState([]);
  const [stockLevels, setStockLevels] = useState([]);
  const [supplies, setSupplies] = useState([]);

  const userRole = localStorage.getItem("userRole");

  // Redirect non-admins
  useEffect(() => {
    if (userRole !== "admin") {
      navigate("/");
    }
  }, [userRole, navigate]);

  // Fetch clients
  useEffect(() => {
    if (userRole !== "admin") return;
    const fetchClients = async () => {
      try {
        const data = await apiClient.get("/clients/");
        setClients(data);
      } catch (err) {
        // Removed console.error
      }
    };
    fetchClients();
  }, [userRole]);

  // Fetch products
  useEffect(() => {
    if (userRole !== "admin") return;
    const fetchProducts = async () => {
      try {
        const data = await apiClient.get("/products/");
        setProducts(data);
      } catch (err) {
        // Removed console.error
      }
    };
    fetchProducts();
  }, [userRole]);

  // Fetch stock
  useEffect(() => {
    if (userRole !== "admin") return;
    const fetchStock = async () => {
      try {
        const data = await apiClient.get("/stock/");
        setStockLevels(data);
      } catch (err) {
        // Removed console.error
      }
    };
    fetchStock();
  }, [userRole]);

  // Fetch supplies (for activity feed)
  useEffect(() => {
    if (userRole !== "admin") return;
    const fetchSupplies = async () => {
      try {
        const data = await apiClient.get("/supplies/");
        setSupplies(data);
      } catch (err) {
        // Removed console.error
      }
    };
    fetchSupplies();
  }, [userRole]);

  // Fetch production runs (with period filter)
  useEffect(() => {
    if (userRole !== "admin") return;

    const fetchProduction = async () => {
      try {
        const runs = await apiClient.get("/production/");

        const productsMap = {};
        products.forEach((p) => (productsMap[p.id] = p));

        const now = new Date();
        const todayStr = now.toISOString().split("T")[0];
        const startOfWeek = new Date(now);
        startOfWeek.setDate(now.getDate() - now.getDay());
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

        const filteredRuns = runs.filter((run) => {
          const runDate = run.produced_at.split("T")[0];
          if (period === "daily") return runDate === todayStr;
          if (period === "weekly")
            return runDate >= startOfWeek.toISOString().split("T")[0];
          if (period === "monthly")
            return runDate >= startOfMonth.toISOString().split("T")[0];
          return true;
        });

        const runsWithProduct = filteredRuns.map((run) => ({
          ...run,
          product: productsMap[run.product_id],
        }));

        setProductionRuns(runsWithProduct);
      } catch (err) {
        // Removed console.error
      }
    };

    fetchProduction();
  }, [userRole, products, period]);

  // Format time ago (e.g., "2 hrs ago")
  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMins = Math.floor(diffMs / (1000 * 60));

    if (diffHrs > 0) return `${diffHrs} hr${diffHrs > 1 ? 's' : ''} ago`;
    if (diffMins > 0) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
    return "just now";
  };

  // Build recent activity feed
  const recentActivity = useMemo(() => {
    const activities = [];

    // New clients
    [...clients]
      .sort((a, b) => (b.id || 0) - (a.id || 0))
      .slice(0, 3)
      .forEach(client => {
        activities.push({
          id: `client-${client.id}`,
          message: `New client: ${client.name}`,
          time: client.created_at ? formatTimeAgo(client.created_at) : "just now",
          type: "client"
        });
      });

    // New production runs
    [...productionRuns]
      .sort((a, b) => new Date(b.produced_at) - new Date(a.produced_at))
      .slice(0, 3)
      .forEach(run => {
        const productName = run.product?.name || `Product ${run.product_id}`;
        activities.push({
          id: `prod-${run.id}`,
          message: `Production: ${productName} x${run.quantity}`,
          time: formatTimeAgo(run.produced_at),
          type: "production"
        });
      });

    // New supplies
    [...supplies]
      .sort((a, b) => new Date(b.delivered_at) - new Date(a.delivered_at))
      .slice(0, 3)
      .forEach(supply => {
        const ingredientName = supply.ingredient_name || `Ingredient ${supply.ingredient_id}`;
        activities.push({
          id: `supply-${supply.id}`,
          message: `Supply received: ${supply.amount} ${supply.unit || 'unit'} ${ingredientName}`,
          time: formatTimeAgo(supply.delivered_at),
          type: "supply"
        });
      });

    // Sort by recency using original timestamps (safe fallback)
    return activities
      .sort((a, b) => {
        const getDate = (item) => {
          if (item.type === 'client') return new Date(item.time.includes('just now') ? Date.now() : item.time);
          if (item.type === 'production') return new Date(productionRuns.find(p => p.id === parseInt(item.id.split('-')[1]))?.produced_at || 0);
          if (item.type === 'supply') return new Date(supplies.find(s => s.id === parseInt(item.id.split('-')[1]))?.delivered_at || 0);
          return 0;
        };
        return getDate(b) - getDate(a);
      })
      .slice(0, 6);
  }, [clients, productionRuns, supplies]);

  // PDF Export
  const handlePdfExport = () => {
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text(`${t("dashboard")} — ${t(period)}`, 20, 20);
    doc.setFontSize(12);
    doc.text(`${t("clients")}: ${clients.length}`, 20, 30);
    doc.text(`${t("products")}: ${products.length}`, 20, 40);
    doc.save("dashboard_summary.pdf");
  };

  if (userRole !== "admin") return null;

  const recentClients = [...clients]
    .sort((a, b) => (b.id || 0) - (a.id || 0))
    .slice(0, 3);

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto space-y-6 bg-[#F5E9D8] min-h-screen">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center">
        <h2 className="text-2xl font-bold text-[#2D2F5E]">{t("dashboard")}</h2>
        <button
          onClick={handlePdfExport}
          className="mt-3 sm:mt-0 flex items-center bg-[#F76B4C] hover:bg-[#E65A3C] text-white px-4 py-2 rounded-lg transition shadow"
        >
          <ArrowDownTrayIcon className="h-5 w-5 mr-1.5" />
          {t("exportPdf")}
        </button>
      </div>

      {/* Stats Cards + Stock */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Clients Card */}
        <div className="bg-white p-4 rounded-xl shadow-sm border border-[#2D2F5E] border-opacity-20">
          <div className="flex items-center mb-2">
            <UserGroupIcon className="h-6 w-6 text-[#2D2F5E] mr-2" />
            <span className="text-lg font-bold text-[#2D2F5E]">{t("clients")}</span>
          </div>
          <p className="text-2xl font-bold text-[#2D2F5E] mb-2">{clients.length}</p>
          {recentClients.length > 0 && (
            <div className="mt-2 pt-2 border-t border-gray-200">
              <p className="text-xs text-[#2D2F5E] opacity-70 mb-1">Recently added:</p>
              <ul className="text-xs space-y-1">
                {recentClients.map(client => (
                  <li key={client.id} className="text-[#2D2F5E] truncate">• {client.name}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Products */}
        <div className="bg-white p-4 rounded-xl shadow-sm border border-[#2D2F5E] border-opacity-20 flex items-center">
          <CubeIcon className="h-8 w-8 text-[#2D2F5E] mr-3 flex-shrink-0" />
          <div>
            <p className="text-sm text-gray-500">{t("products")}</p>
            <p className="text-xl font-bold text-[#2D2F5E]">{products.length}</p>
          </div>
        </div>

        {/* Stock Card */}
        <div className="lg:col-span-2 bg-white p-4 rounded-xl shadow-sm border border-[#2D2F5E] border-opacity-20">
          <div className="flex items-center mb-3">
            <span className="text-lg font-bold text-[#2D2F5E]">🧂 {t("stock")}</span>
          </div>
          {stockLevels.length > 0 ? (
            <div className="space-y-2">
              {stockLevels.map((item, idx) => (
                <div 
                  key={item.id} 
                  className={`flex justify-between py-1.5 ${
                    idx < stockLevels.length - 1 ? "border-b border-gray-100" : ""
                  }`}
                >
                  <span className="text-[#2D2F5E] font-medium">{item.name}:</span>
                  <span className="font-bold text-[#2D2F5E]">
                    {item.available.toFixed(1)} {item.unit}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-[#2D2F5E] text-sm italic">Loading...</p>
          )}
        </div>
      </div>

      {/* Recent Activity Feed */}
      <div className="bg-white rounded-xl shadow-sm border border-[#2D2F5E] border-opacity-20 p-4">
        <h3 className="font-bold text-[#2D2F5E] mb-3">🔔 Recent Activity</h3>
        {recentActivity.length > 0 ? (
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {recentActivity.map((activity) => (
              <div key={activity.id} className="flex justify-between text-sm">
                <span className="text-[#2D2F5E]">{activity.message}</span>
                <span className="text-gray-500">{activity.time}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-[#2D2F5E] italic">No recent activity</p>
        )}
      </div>

      {/* Production Section */}
      <div className="bg-white rounded-xl shadow-sm border border-[#2D2F5E] border-opacity-20 p-4">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <span className="font-medium text-[#2D2F5E]">{t("filterBy")}:</span>
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="border border-[#2D2F5E] rounded px-2 py-1 bg-white text-[#2D2F5E] appearance-none min-w-[100px]"
          >
            <option value="all">{t("all")}</option>
            <option value="daily">{t("daily")}</option>
            <option value="weekly">{t("weekly")}</option>
            <option value="monthly">{t("monthly")}</option>
          </select>
        </div>

        <div className="text-sm text-[#2D2F5E] mb-3">
          {t("productionRuns")}: {productionRuns.length}
        </div>

        {productionRuns.length > 0 ? (
          <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
            {productionRuns.map((run) => (
              <div
                key={run.id}
                className="border border-[#2D2F5E] border-opacity-20 p-3 rounded-lg bg-[#F5E9D8]"
              >
                <div className="font-semibold text-[#2D2F5E]">
                  {run.product?.name || `Product ${run.product_id}`}
                </div>
                <div className="text-sm text-[#2D2F5E] mt-1">
                  📅 {run.produced_at.split("T")[0]}
                </div>
                <div className="text-sm text-[#2D2F5E]">
                  🔢 <span className="font-medium">Batch:</span> {run.batch_code || "–"}
                </div>
                <div className="text-sm text-[#2D2F5E]">
                  💬 <span className="font-medium">Remarks:</span> {run.notes || "–"}
                </div>
                <div className="text-sm text-[#2D2F5E] mt-1">
                  ✅ <span className="font-medium">Qty:</span> {run.quantity}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-[#2D2F5E] text-sm italic">{t("noProductionData")}</p>
        )}
      </div>
    </div>
  );
}

export default Dashboard;